import React from 'react';
import { motion } from 'framer-motion';

interface DataStream {
  icon: string;
  label: string;
  value?: number;
}

interface DataStreamBarsProps {
  dataStreams: DataStream[];
  partnerPrimaryColor?: string;
  editable?: boolean;
  onChange?: (index: number, value: number) => void;
}

export const DataStreamBars: React.FC<DataStreamBarsProps> = ({
  dataStreams,
  partnerPrimaryColor = '#B100FF',
  editable = false,
  onChange,
}) => {
  // Get color based on value (0-100)
  const getColorForValue = (value: number): string => {
    if (value >= 70) {
      // Green zone (70-100)
      return '#22C55E'; // green-500
    } else if (value >= 40) {
      // Yellow zone (40-70)
      return '#EAB308'; // yellow-500
    } else {
      // Red zone (0-40)
      return '#EF4444'; // red-500
    }
  };

  // Get gradient for bar background
  const getGradientForValue = (value: number): string => {
    const color = getColorForValue(value);
    return `linear-gradient(to right, ${color}dd ${value}%, #e5e7eb ${value}%)`;
  };

  // Get label for value range
  const getLabelForValue = (value: number): string => {
    if (value >= 80) return 'Exceptional';
    if (value >= 70) return 'Strong';
    if (value >= 60) return 'Good';
    if (value >= 40) return 'Moderate';
    if (value >= 20) return 'At Risk';
    return 'Significant Risk';
  };

  return (
    <div className="space-y-4 w-full">
      {dataStreams.map((stream, index) => {
        const value = stream.value ?? 95;
        const delay = index * 0.1;

        return (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay }}
            className="space-y-2"
          >
            {/* Icon and Label */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{stream.icon}</span>
                <span className="font-semibold text-sm sm:text-base text-foreground">
                  {stream.label}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  {getLabelForValue(value)}
                </span>
                <span 
                  className="text-lg font-bold tabular-nums min-w-[3ch]"
                  style={{ color: getColorForValue(value) }}
                >
                  {value}
                </span>
              </div>
            </div>

            {/* Bar or Slider */}
            {editable ? (
              <div className="space-y-2">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={value}
                  onChange={(e) => onChange?.(index, parseInt(e.target.value))}
                  className="w-full h-8 rounded-lg appearance-none cursor-grab active:cursor-grabbing border border-border"
                  style={{
                    background: `linear-gradient(to right, ${getColorForValue(value)} ${value}%, #e5e7eb ${value}%)`,
                  }}
                />
                <style>{`
                  input[type='range']::-webkit-slider-thumb {
                    appearance: none;
                    width: 0;
                    height: 0;
                    opacity: 0;
                  }
                  input[type='range']::-moz-range-thumb {
                    width: 0;
                    height: 0;
                    opacity: 0;
                    border: none;
                    background: transparent;
                  }
                `}</style>
              </div>
            ) : (
              <div className="relative h-8 rounded-lg overflow-hidden bg-muted border border-border">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${value}%` }}
                  transition={{ duration: 1, delay: delay + 0.2, ease: "easeOut" }}
                  className="absolute inset-y-0 left-0 rounded-lg"
                  style={{ 
                    background: getColorForValue(value),
                    opacity: 0.9,
                  }}
                />
                {/* Grid lines for reference */}
                <div className="absolute inset-0 flex">
                  {[25, 50, 75].map((mark) => (
                    <div
                      key={mark}
                      className="border-r border-border/30"
                      style={{ marginLeft: `${mark}%` }}
                    />
                  ))}
                </div>
                {/* Value text inside bar */}
                <div className="absolute inset-0 flex items-center justify-end pr-3">
                  <span className="text-xs font-medium text-muted-foreground/70">
                    {value}/100
                  </span>
                </div>
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
};

